import { updateFormData } from 'js/actions';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import useSinksAdapter from 'new/state/sinks/adapter/useSinksAdapter';
import useSourcesAdapter from 'new/state/sources/adapter/useSourcesAdapter';
import useSources from 'new/state/sources/repository/useSources';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import useNexsetsAdapter from 'new/state/nexsets/useNexsetsAdapter';
import useFlows from 'new/state/flows/repository/useFlows';
import UIUtils from 'js/lib/UIUtils';

export default function useFlowsAdapter() {
  const { requestSinkById } = useSinksAdapter();
  const { refreshPipelineById } = usePipelinesAdapter();
  const { requestNexset } = useNexsetsAdapter();
  const { getSourceById } = useSources();
  const { requestSourceById } = useSourcesAdapter();
  const { getFlowById } = useFlows();

  function pollNexset(onNexsetFound) {
    let requesting = false;
    let stop = false;
    async function requestFlow(flowId) {
      if (stop) return false;
      await UIUtils.wait(2000);
      const flowResult = await refreshPipelineById(flowId);
      const nexsetResult = flowResult?.entities?.dataSets?.[flowResult?.result?.data_sets?.id?.[0]];
      if (nexsetResult) {
        onNexsetFound(nexsetResult);
        return true;
      }
      requestFlow(flowId);
      return false;
    }

    function request(flowId: number) {
      if (!requesting) {
        requesting = true;
        stop = false;
        requestFlow(flowId);
      }
    }

    request.cancel = () => {
      stop = true;
    };

    return request;
  }

  async function requestResources({ resourceType, id, flowId, nexsetId = undefined }) {
    let sink, source, nexset;
    let flow = getFlowById(flowId);
    if (resourceType === 'sink') {
      if (id) {
        const sinkResult = await requestSinkById({
          id,
          aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
          ignoreErrors: false,
        });
        sink = sinkResult?.entities?.sinks?.[id];
        updateFormData({
          credentialId: sink.data_credentials,
          selectedConnection: sink.connector_type,
        });
        nexset = await requestNexset(sink.data_set_id);
      }
    }
    if (!flowId && sink?.origin_node_id) {
      flowId = sink?.origin_node_id;
    }
    if (flowId && !flow) {
      const flowResult = await refreshPipelineById(flowId);
      flow = flowResult?.entities?.flowNodes?.[flowId];
      if (!nexset && nexsetId) {
        nexset = flowResult?.entities?.dataSets?.[nexsetId];
      }
    }
    // When attaching a sink to a specific RUT (multi-RUT CDC flows), the
    // caller passes nexsetId as a prop. If the flow node was already cached
    // we skipped the refresh above and never resolved the nexset — fetch it
    // directly so the caller doesn't fall through to a "first dataset"
    // fallback that ignores the user's selection.
    if (nexsetId && !nexset) {
      nexset = await requestNexset(nexsetId);
    }
    const sourceId = flow?.data_source_id;
    source = getSourceById(sourceId);
    const sourceRequestId = (() => {
      if (resourceType === 'source' && id) return id;
      if (resourceType === 'sink' && sourceId && !getSourceById(sourceId)) {
        return sourceId;
      }
    })();

    if (sourceRequestId && !source?.source_config) {
      const sourceResult = (await requestSourceById({
        id: sourceRequestId,
        aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
        ignoreErrors: false,
      })) as any;
      source = sourceResult?.entities?.sources?.[sourceRequestId];
    }
    return { source, sink, nexset };
  }
  return {
    requestResources,
    pollNexset,
  };
}
